/**
 * Synchronization service for offline/online data sync
 */

import { offlineStorage, SyncQueueItem } from './offlineStorage';
import { api } from './api';
import { withInspectionId } from '../pages/lite/liteSubmitReplay';

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

export interface SyncProgress {
  total: number;
  completed: number;
  failed: number;
  inProgress: boolean;
}

export interface ConflictResolution {
  strategy: 'last-write-wins' | 'merge' | 'ask-user';
  resolver?: (local: any, remote: any) => any;
}

/**
 * The reason a queued request failed, as the server said it (LOG-1023-05):
 * FastAPI's `detail` string, or its 422 field list as "field: message", else
 * the transport's message. Jim's and Chris's phones replayed inspections with
 * no site id for a week while the badge said only "failed to send".
 */
export function serverReason(error: unknown): string {
  const e = error as {
    message?: string;
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        const loc = Array.isArray(d?.loc)
          ? d.loc.filter((x: unknown) => x !== 'body').join('.')
          : '';
        return loc ? `${loc}: ${d?.msg ?? ''}` : String(d?.msg ?? '');
      })
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  const status = e?.response?.status;
  return (
    e?.message || (status ? `Request failed (${status})` : 'Request failed')
  );
}

class SyncService {
  private syncInProgress = false;
  private syncInterval: number | null = null;
  private onlineStatusListeners: Array<(online: boolean) => void> = [];
  private syncProgressListeners: Array<(progress: SyncProgress) => void> = [];
  private conflictResolution: ConflictResolution = {
    strategy: 'last-write-wins',
  };

  constructor() {
    this.setupEventListeners();
  }

  private setupEventListeners() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.notifyOnlineStatus(true);
      this.startBackgroundSync();
    });

    window.addEventListener('offline', () => {
      this.notifyOnlineStatus(false);
      this.stopBackgroundSync();
    });

    // Listen for visibility changes to pause/resume sync
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopBackgroundSync();
      } else if (navigator.onLine) {
        this.startBackgroundSync();
      }
    });
  }

  // Online/Offline Status Management
  isOnline(): boolean {
    return navigator.onLine;
  }

  onOnlineStatusChange(callback: (online: boolean) => void): () => void {
    this.onlineStatusListeners.push(callback);
    // Return unsubscribe function
    return () => {
      this.onlineStatusListeners = this.onlineStatusListeners.filter(
        (cb) => cb !== callback
      );
    };
  }

  private notifyOnlineStatus(online: boolean) {
    this.onlineStatusListeners.forEach((callback) => callback(online));
  }

  // Sync Progress Management
  onSyncProgress(callback: (progress: SyncProgress) => void): () => void {
    this.syncProgressListeners.push(callback);
    return () => {
      this.syncProgressListeners = this.syncProgressListeners.filter(
        (cb) => cb !== callback
      );
    };
  }

  private notifySyncProgress(progress: SyncProgress) {
    this.syncProgressListeners.forEach((callback) => callback(progress));
  }

  // Background Sync Management
  startBackgroundSync(intervalMs = 60000) {
    if (this.syncInterval) return;

    // Initial sync
    this.syncAll();

    // Set up periodic sync
    this.syncInterval = window.setInterval(() => {
      if (this.isOnline() && !this.syncInProgress) {
        this.syncAll();
      }
    }, intervalMs);
  }

  stopBackgroundSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Main Sync Logic. The background tick replays pending (and reclaimed
  // "syncing") items only; a permanently failed item — a 404/400/422 the
  // server will answer the same way every time — is replayed only when the
  // inspector taps Retry now (includeFailed). Folding failed items into every
  // tick had Robert Keller's PC posting one dead inspection every 7-8 s for
  // three days, 8,938 × 404 and a burst of 429s (prod audit, 10/23).
  async syncAll({ includeFailed = false } = {}): Promise<void> {
    if (this.syncInProgress || !this.isOnline()) return;

    this.syncInProgress = true;

    try {
      // Get all pending items from sync queue. An item left in 'syncing'
      // (tab closed or connection dropped mid-request) was never picked up
      // again and never counted — reclaim it as pending on every tick.
      const pendingItems = await offlineStorage.getSyncQueue('pending');
      const failedItems = includeFailed
        ? await offlineStorage.getSyncQueue('failed')
        : [];
      const stuckItems = await offlineStorage.getSyncQueue('syncing');
      const itemsToSync = [...pendingItems, ...stuckItems, ...failedItems];

      if (itemsToSync.length === 0) {
        this.syncInProgress = false;
        return;
      }

      const progress: SyncProgress = {
        total: itemsToSync.length,
        completed: 0,
        failed: 0,
        inProgress: true,
      };

      this.notifySyncProgress(progress);

      // Process items with exponential backoff for retries
      for (const item of itemsToSync) {
        try {
          await this.syncSingleItem(item);
          progress.completed++;
        } catch (error) {
          progress.failed++;
          await this.handleSyncError(item, error);
        }

        this.notifySyncProgress({ ...progress });
      }

      // (The former second pass over POST /inspections items was removed:
      // every item is already replayed once per tick above, and the extra
      // pass re-sent the same body — audit 2026-09-04, H6.)
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      this.syncInProgress = false;
      this.notifySyncProgress({
        total: 0,
        completed: 0,
        failed: 0,
        inProgress: false,
      });
    }
  }

  private async syncSingleItem(item: SyncQueueItem): Promise<void> {
    await offlineStorage.updateSyncQueueItem(item.id, { status: 'syncing' });

    // Make the API request
    await this.makeRequest(item);

    // Mark as completed
    await offlineStorage.updateSyncQueueItem(item.id, {
      status: 'completed',
    });

    // Clean up completed items periodically
    if (Math.random() < 0.1) {
      await offlineStorage.removeSyncQueueItem(item.id);
    }
  }

  private async makeRequest(item: SyncQueueItem): Promise<any> {
    let resultId = item.resultId;
    let response: any;

    // The primary request runs once. Its returned id is persisted before any
    // follow-up so a later retry resumes instead of re-creating.
    if (!resultId) {
      const config: any = {
        method: item.method,
        url: item.url,
        headers: item.headers,
      };
      if (item.body) {
        config.data = item.body;
      }
      response = await api.request(config);
      resultId = response?.data?.id;
      if (item.followUps?.length && resultId) {
        await offlineStorage.updateSyncQueueItem(item.id, { resultId });
      }
    }

    const followUps = item.followUps ?? [];
    for (let i = item.followUpsDone ?? 0; i < followUps.length; i++) {
      if (!resultId) {
        throw new Error(
          'Follow-up requests need the id the primary request returned'
        );
      }
      const step = followUps[i];
      await api.request({
        method: step.method,
        url: withInspectionId(step.url, resultId),
        data: step.body ? withInspectionId(step.body, resultId) : undefined,
      });
      await offlineStorage.updateSyncQueueItem(item.id, {
        followUpsDone: i + 1,
      });
    }

    return response;
  }

  private async handleSyncError(
    item: SyncQueueItem,
    error: any
  ): Promise<void> {
    const isRetryable = this.isRetryableError(error);
    const newRetryCount = item.retryCount + 1;

    if (isRetryable && newRetryCount < item.maxRetries) {
      await offlineStorage.updateSyncQueueItem(item.id, {
        status: 'pending',
        retryCount: newRetryCount,
        error: serverReason(error),
      });
      // The next background tick retries it; a detached setTimeout retry
      // used to run the stale pre-retry item a second time (audit H6).
    } else {
      // Max retries reached or non-retryable error
      await offlineStorage.updateSyncQueueItem(item.id, {
        status: 'failed',
        error: serverReason(error),
      });
    }
  }

  private isRetryableError(error: any): boolean {
    if (!error.response) return true; // Network error

    const status = error.response.status;
    // Retry on 5xx errors and specific 4xx errors
    return status >= 500 || status === 408 || status === 429;
  }

  // Public so the conflict-strategy tests can drive it directly; nothing in
  // the sync loop calls it since the second replay pass was removed.
  async resolveConflict(local: any, remote: any): Promise<any> {
    switch (this.conflictResolution.strategy) {
      case 'last-write-wins': {
        // Compare timestamps and keep the newer one
        const localTime = new Date(
          local.updated_at || local.created_at
        ).getTime();
        const remoteTime = new Date(
          remote.updated_at || remote.created_at
        ).getTime();
        return localTime > remoteTime ? local : remote;
      }

      case 'merge':
        // Merge strategy - combine non-conflicting fields
        return this.mergeData(local, remote);

      case 'ask-user':
        // In a real app, this would show a UI for user to resolve
        if (this.conflictResolution.resolver) {
          return this.conflictResolution.resolver(local, remote);
        }
        return local; // Default to local version

      default:
        return local;
    }
  }

  private mergeData(local: any, remote: any): any {
    const merged = { ...remote };

    // Merge arrays by combining unique items
    Object.keys(local).forEach((key) => {
      if (Array.isArray(local[key]) && Array.isArray(remote[key])) {
        merged[key] = [...new Set([...remote[key], ...local[key]])];
      } else if (local[key] !== remote[key]) {
        // For conflicting scalar values, prefer local (user's changes)
        merged[key] = local[key];
      }
    });

    return merged;
  }

  // Public API for adding items to sync queue
  async queueRequest(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    data?: any,
    options?: {
      headers?: Record<string, string>;
      maxRetries?: number;
    }
  ): Promise<string> {
    const item = {
      method,
      url,
      body: data,
      headers: options?.headers,
      timestamp: Date.now(),
      maxRetries: options?.maxRetries || 3,
    };

    const id = await offlineStorage.addToSyncQueue(item);

    // Try to sync immediately if online
    if (this.isOnline() && !this.syncInProgress) {
      this.syncAll();
    }

    return id;
  }

  // Set conflict resolution strategy
  setConflictResolution(resolution: ConflictResolution) {
    this.conflictResolution = resolution;
  }

  // Get sync statistics
  async getSyncStats(): Promise<{
    pending: number;
    failed: number;
    lastSync: number | null;
  }> {
    const pending = await offlineStorage.getSyncQueue('pending');
    const stuck = await offlineStorage.getSyncQueue('syncing');
    const failed = await offlineStorage.getSyncQueue('failed');

    return {
      // In-flight items count as pending: a request interrupted mid-sync
      // is still unsent work the banner must show.
      pending: pending.length + stuck.length,
      failed: failed.length,
      lastSync: null, // Could track this separately
    };
  }
}

export const syncService = new SyncService();
